import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { 
  X, Link2, MessageCircle, Send, Check, Loader2,
  Copy, ExternalLink, Search, Users, Mail
} from 'lucide-react';
// Phase B5 P2 §3 — Community share tab
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Social media icons as SVG components
const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const ShareModal = ({
  isOpen,
  onClose,
  shareType = 'product', // 'product', 'post', 'profile', 'event'
  shareData = {}, // { id, title, description, url }
  onShareToFeed = null, // Sprint 1.0c V1.a-bis hotfix #4 Bug 7 — optional cb to publish into Actualité
}) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  // Sprint 1.0c V1.a-bis hotfix #5 (Bug G): default tab = Actualité quand onShareToFeed est dispo,
  // sinon Réseaux sociaux comme avant.
  const [activeTab, setActiveTab] = useState(onShareToFeed ? 'feed' : 'external');
  const [copied, setCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [sendingTo, setSendingTo] = useState(null);
  const [sentTo, setSentTo] = useState([]);
  // Sprint 1.0c V1.a-bis hotfix #6 (Bug N) — search ANY OpenSapiens user, not
  // just existing conversations / followed. Regression introduced by Bug E
  // (delete conversation): once a thread is soft-deleted, the user disappeared
  // from the picker. Now we live-search via /api/social/search-users.
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  const searchTimerRef = React.useRef(null);

  // Phase B5 P2 §3 — Community share tab state
  const [myCommunities, setMyCommunities] = useState([]);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [selectedCommunityId, setSelectedCommunityId] = useState('');
  const [communityMessage, setCommunityMessage] = useState('');
  const [communityPosting, setCommunityPosting] = useState(false);
  const [communityPostedTo, setCommunityPostedTo] = useState(null);

  // Brief Prospection Phase 1 — lien universel /api/@{handle}/e/{slug} pour les
  // events (aperçus OG garantis). Résolu au moment de l'ouverture : si le
  // caller ne fournit pas handle+slug, on les récupère via /public/event/{id}.
  const [eventUniversalUrl, setEventUniversalUrl] = useState(null);
  React.useEffect(() => {
    if (!isOpen || shareType !== 'event' || !shareData.id) return;
    const origin = window.location.origin;
    if (shareData.handle && shareData.slug) {
      setEventUniversalUrl(`${origin}/api/@${shareData.handle}/e/${shareData.slug}`);
      return;
    }
    let alive = true;
    axios.get(`${API}/public/event/${shareData.id}`)
      .then(({ data }) => {
        if (!alive) return;
        const handle = data?.expert?.handle;
        const slug = data?.slug;
        if (data?.visibility === 'public' && handle && slug) {
          setEventUniversalUrl(`${origin}/api/@${handle}/e/${slug}`);
        } else {
          setEventUniversalUrl(`${origin}/event/${shareData.id}`);
        }
      })
      .catch(() => { if (alive) setEventUniversalUrl(`${origin}/event/${shareData.id}`); });
    return () => { alive = false; };
  }, [isOpen, shareType, shareData.id, shareData.handle, shareData.slug]);

  // Generate share URL
  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    if (shareType === 'product') {
      // Brief LOT3 — page publique dédiée produit (consultable sans compte).
      return `${baseUrl}/product/${shareData.id}`;
    } else if (shareType === 'post') {
      // Brief LOT3 — page publique dédiée (consultable sans compte).
      return `${baseUrl}/post/${shareData.id}`;
    } else if (shareType === 'profile') {
      // Brief 27 oct B.1 — lien universel OG /api/@handle (page publique sans
      // compte, aperçu WhatsApp/LinkedIn) dès qu'un handle existe.
      return shareData.handle ? `${baseUrl}/api/@${shareData.handle}` : `${baseUrl}/app/profile/${shareData.id}`;
    } else if (shareType === 'community') {
      // Brief 27 oct B.2 — la communauté n'a pas de page publique : le lien
      // ouvre l'app (login puis retour automatique sur la communauté).
      return `${baseUrl}/app/community/${shareData.id}`;
    } else if (shareType === 'event') {
      // Brief Prospection Phase 1 — lien universel OG (remplace l'ancien
      // /app/profile/{userId}#event-{eventId}). Fallback : page publique
      // /event/{id} tant que la résolution handle+slug n'est pas terminée.
      return eventUniversalUrl || `${baseUrl}/event/${shareData.id}`;
    }
    return shareData.url || window.location.href;
  };

  // Sprint 1.0c V1.a-bis hotfix #4 — Bug 7 : Actualité publish state
  const [actuPosting, setActuPosting] = useState(false);
  const [actuPosted, setActuPosted] = useState(false);
  const [actuMessage, setActuMessage] = useState('');

  const shareUrl = getShareUrl();
  const shareTitle = shareData.title || 'Découvrez sur OpenSapiens';
  const shareText = shareData.description || 'Rejoignez-moi sur OpenSapiens !';

  // Copy link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Share to social platforms
  const handleShareLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleShareTwitter = () => {
    const text = `${shareTitle} - ${shareText}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleShareWhatsApp = () => {
    const text = `${shareTitle}\n${shareText}\n${shareUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  // Brief 27 oct B.1/B.2 — partage par email (option demandée)
  const handleShareEmail = () => {
    const subject = `${shareTitle} — OpenSapiens`;
    const body = `${shareText}\n\n${shareUrl}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // Load conversations for DM sharing
  const loadConversations = async () => {
    setLoadingConversations(true);
    try {
      const [convResponse, followingResponse] = await Promise.all([
        axios.get(`${API}/messages/conversations`, { withCredentials: true }),
        axios.get(`${API}/social/following`, { withCredentials: true }),
      ]);
      // Backend conversations endpoint returns `partner` (not `other_user`). Normalize here
      // so the rest of the modal can use a single field name.
      const myId = currentUser?.user_id;
      const convs = (convResponse.data.conversations || []).map((c) => ({
        ...c,
        other_user: c.other_user || c.partner,
      }));
      const following = followingResponse.data.following || [];

      // Sprint 1.0b-γ fix F23.3 — exclude self from the recipient picker.
      // Bug: posts shared via DM could only be sent to oneself when the legacy
      // conversation listing returned the user's own row.
      const filterOutSelf = (uid) => !myId || uid !== myId;

      // Merge: include conversations + followed users not in conversations
      const convUserIds = new Set(
        convs
          .map((c) => c.other_user?.user_id)
          .filter((uid) => filterOutSelf(uid))
      );
      const followedNotInConv = following
        .filter((f) => filterOutSelf(f.user_id) && !convUserIds.has(f.user_id))
        .map((f) => ({
          conversation_id: `follow_${f.user_id}`,
          other_user: { user_id: f.user_id, display_name: f.display_name, avatar: f.avatar },
          is_following: true,
        }));

      setConversations([
        ...convs.filter((c) => filterOutSelf(c.other_user?.user_id)),
        ...followedNotInConv,
      ]);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  };

  // Send via DM
  const handleSendDM = async (userId, displayName) => {
    setSendingTo(userId);
    try {
      // Sprint 1.0c V1.a-bis hotfix #5 (Bug I) — events are sent with an
      // [EVENT_SHARE:<event_id>] marker so the Messages renderer can fetch
      // the event and display a rich preview card (image, title, date, CTA)
      // instead of raw text.
      let message;
      if (shareType === 'event' && shareData.id) {
        const intro = shareText && shareText !== `${shareTitle}` ? `\n${shareText}` : '';
        message = `[EVENT_SHARE:${shareData.id}]\n📅 ${shareTitle}${intro}\n\n${shareUrl}`;
      } else {
        message = `🔗 ${shareTitle}\n${shareText}\n\n${shareUrl}`;
      }
      await axios.post(`${API}/messages/${userId}`, { content: message }, { withCredentials: true });
      setSentTo([...sentTo, userId]);
    } catch (error) {
      console.error('Error sending DM:', error);
    } finally {
      setSendingTo(null);
    }
  };

  // Load conversations when switching to DM tab
  React.useEffect(() => {
    if (activeTab === 'dm' && conversations.length === 0) {
      loadConversations();
    }
  }, [activeTab]);

  // Phase B5 P2 §3 — Load user's communities when switching to Community tab
  React.useEffect(() => {
    if (activeTab !== 'community') return;
    if (myCommunities.length > 0 || !currentUser?.user_id) return;
    setLoadingCommunities(true);
    axios
      .get(`${API}/users/${currentUser.user_id}/communities`, { withCredentials: true })
      .then(({ data }) => {
        const list = (data.communities || []).filter((c) => c.is_member);
        setMyCommunities(list);
        if (list.length > 0 && !selectedCommunityId) setSelectedCommunityId(list[0].community_id);
      })
      .catch(() => setMyCommunities([]))
      .finally(() => setLoadingCommunities(false));
  }, [activeTab, currentUser?.user_id]);

  // Phase B5 P2 §3 — Publish to selected community wall
  const handleShareToCommunity = async () => {
    if (!selectedCommunityId || communityPosting) return;
    setCommunityPosting(true);
    try {
      const intro = (communityMessage || '').trim() || `🔗 ${shareTitle}`;
      // Use shortcode markers so the wall renderer can attach a rich preview
      let content;
      if (shareType === 'product' && shareData.id) {
        content = `${intro}\n\n[product:${shareData.id}:${shareTitle}]\n${shareUrl}`;
      } else if (shareType === 'event' && shareData.id) {
        content = `${intro}\n\n[EVENT_SHARE:${shareData.id}]\n📅 ${shareTitle}\n${shareUrl}`;
      } else if (shareType === 'community' && shareData.id) {
        // Brief 27 oct B.2 — card communauté cliquable dans le mur cible
        content = `${intro}\n\n[community:${shareData.id}:${shareTitle}]`;
      } else {
        content = `${intro}\n\n${shareUrl}`;
      }
      await axios.post(
        `${API}/communities/${selectedCommunityId}/posts`,
        { content, media_url: null },
        { withCredentials: true }
      );
      setCommunityPostedTo(selectedCommunityId);
    } catch (error) {
      console.error('Error sharing to community:', error);
    } finally {
      setCommunityPosting(false);
    }
  };

  // Sprint 1.0c V1.a-bis hotfix #6 (Bug N) — debounced live search of ANY user.
  React.useEffect(() => {
    if (activeTab !== 'dm') return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const q = searchQuery.trim();
    if (q.length < 1) {
      setUserSearchResults([]);
      setUserSearching(false);
      return;
    }
    setUserSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `${API}/social/search-users?q=${encodeURIComponent(q)}`,
          { withCredentials: true }
        );
        setUserSearchResults(data.users || []);
      } catch {
        setUserSearchResults([]);
      } finally {
        setUserSearching(false);
      }
    }, 250);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery, activeTab]);

  if (!isOpen) return null;

  // Sprint 1.0c V1.a-bis hotfix #6 (Bug N) — merge existing conversations
  // (matched by query) + live API results, dedup by user_id, exclude self.
  const myId = currentUser?.user_id;
  const lowerQ = searchQuery.trim().toLowerCase();
  const baseConvs = conversations.filter((conv) => {
    if (!conv.other_user?.user_id || conv.other_user.user_id === myId) return false;
    if (!lowerQ) return true;
    return (conv.other_user.display_name || '').toLowerCase().includes(lowerQ);
  });
  const knownIds = new Set(baseConvs.map((c) => c.other_user?.user_id));
  const extraFromSearch = (userSearchResults || [])
    .filter((u) => u.user_id && u.user_id !== myId && !knownIds.has(u.user_id))
    .map((u) => ({
      conversation_id: `search_${u.user_id}`,
      other_user: { user_id: u.user_id, display_name: u.display_name, avatar: u.avatar },
      is_search_match: true,
    }));
  const filteredConversations = [...baseConvs, ...extraFromSearch];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="share-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Partager</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
            aria-label="Fermer"
            data-testid="share-modal-close"
          >
            <X className="h-4 w-4 text-gray-500 dark:text-gray-300" />
          </button>
        </div>

        {/* Preview */}
        <div className="p-4 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white">
              <Link2 className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-white truncate">{shareTitle}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{shareText}</p>
            </div>
          </div>
        </div>

        {/* Tabs — Sprint 1.0c V1.a-bis hotfix #5 (Bug G): order = Actualité (interne push) → DM (relationnel) → Réseaux sociaux (externe) */}
        <div className="flex border-b border-gray-100 dark:border-gray-700">
          {/* Sprint 1.0c V1.a-bis hotfix #4 — Bug 7 : Actualité tab (only when onShareToFeed handler is passed) */}
          {onShareToFeed && (
            <button
              onClick={() => setActiveTab('feed')}
              className={`flex-1 py-3 px-1 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'feed'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="share-tab-feed"
            >
              <Send className="h-4 w-4 inline mr-2" />
              Actualité
            </button>
          )}
          <button
            onClick={() => setActiveTab('dm')}
            className={`flex-1 py-3 px-1 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'dm' 
                ? 'text-purple-600 border-b-2 border-purple-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="share-tab-dm"
          >
            <MessageCircle className="h-4 w-4 inline mr-1.5" />
            <span className="sm:hidden">Message</span><span className="hidden sm:inline">Message privé</span>
          </button>
          {/* Phase B5 P2 §3 — Community tab */}
          <button
            onClick={() => setActiveTab('community')}
            className={`flex-1 py-3 px-1 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'community'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="share-tab-community"
          >
            <Users className="h-4 w-4 inline mr-2" />
            Communauté
          </button>
          <button
            onClick={() => setActiveTab('external')}
            className={`flex-1 py-3 px-1 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'external' 
                ? 'text-purple-600 border-b-2 border-purple-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="share-tab-external"
          >
            <ExternalLink className="h-4 w-4 inline mr-1.5" />
            <span className="sm:hidden">Réseaux</span><span className="hidden sm:inline">Réseaux sociaux</span>
          </button>
        </div>

        {/* External Share Options */}
        {activeTab === 'external' && (
          <div className="p-4 space-y-3">
            {/* Copy Link */}
            <button
              onClick={handleCopyLink}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/60 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-testid="copy-link-btn"
            >
              <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                {copied ? <Check className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5 text-gray-600 dark:text-gray-200" />}
              </div>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {copied ? 'Lien copié !' : 'Copier le lien'}
              </span>
            </button>

            {/* LinkedIn */}
            <button
              onClick={handleShareLinkedIn}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 transition-colors"
              data-testid="share-linkedin-btn"
            >
              <div className="w-10 h-10 rounded-full bg-[#0A66C2] flex items-center justify-center text-white">
                <LinkedInIcon />
              </div>
              <span className="font-medium text-gray-900 dark:text-gray-100">Partager sur LinkedIn</span>
            </button>

            {/* Twitter/X */}
            <button
              onClick={handleShareTwitter}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
              data-testid="share-twitter-btn"
            >
              <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white">
                <TwitterIcon />
              </div>
              <span className="font-medium text-gray-900 dark:text-gray-100">Partager sur X</span>
            </button>

            {/* WhatsApp */}
            <button
              onClick={handleShareWhatsApp}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors"
              data-testid="share-whatsapp-btn"
            >
              <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center text-white">
                <WhatsAppIcon />
              </div>
              <span className="font-medium text-gray-900 dark:text-gray-100">Partager sur WhatsApp</span>
            </button>

            {/* Brief 27 oct B.1 — Email */}
            <button
              onClick={handleShareEmail}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
              data-testid="share-email-btn"
            >
              <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white">
                <Mail className="h-5 w-5" />
              </div>
              <span className="font-medium text-gray-900 dark:text-gray-100">Envoyer par email</span>
            </button>
          </div>
        )}

        {/* DM Share Options */}
        {activeTab === 'dm' && (
          <div className="p-4">
            {/* Search — Sprint 1.0c V1.a-bis hotfix #6 (Bug N): hint that ANY user is searchable */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un contact (nom ou email)..."
                className="pl-10"
                data-testid="share-dm-search-input"
              />
              {userSearching && (
                <Loader2 className="h-3.5 w-3.5 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-purple-400" />
              )}
            </div>

            {/* Conversations List */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {loadingConversations ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-8 text-gray-500" data-testid="share-dm-empty">
                  <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  {searchQuery.trim().length === 0 ? (
                    <>
                      <p className="text-sm">Aucune conversation</p>
                      <p className="text-xs mt-1">Tape un nom ou un email pour trouver un contact</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm">Aucun utilisateur trouvé</p>
                      <p className="text-xs mt-1">Vérifie l&apos;orthographe ou essaie un email</p>
                    </>
                  )}
                </div>
              ) : (
                filteredConversations.map((conv, idx) => {
                  const isSent = sentTo.includes(conv.other_user?.user_id);
                  const isSending = sendingTo === conv.other_user?.user_id;
                  const isNewContact = !!conv.is_search_match;

                  return (
                    <div
                      key={conv.conversation_id || conv.other_user?.user_id || `conv-${idx}`}
                      className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors"
                      data-testid={`share-dm-row-${conv.other_user?.user_id || idx}`}
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={conv.other_user?.avatar} />
                        <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm">
                          {conv.other_user?.display_name?.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {conv.other_user?.display_name}
                        </p>
                        {isNewContact && (
                          <p className="text-[10px] text-purple-600 font-medium uppercase tracking-wider mt-0.5">
                            Nouveau contact
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleSendDM(conv.other_user?.user_id, conv.other_user?.display_name)}
                        disabled={isSending || isSent}
                        className={isSent 
                          ? 'bg-green-500 hover:bg-green-500' 
                          : 'bg-purple-500 hover:bg-purple-600'
                        }
                      >
                        {isSending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : isSent ? (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Envoyé
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-1" />
                            Envoyer
                          </>
                        )}
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Phase B5 P2 §3 — Community share panel */}
        {activeTab === 'community' && (
          <div className="p-4 space-y-3" data-testid="share-community-panel">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Publie dans le mur d&apos;une communauté dont tu es membre.
            </p>
            {loadingCommunities ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
              </div>
            ) : myCommunities.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="share-community-empty">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Tu n&apos;es membre d&apos;aucune communauté</p>
                <button
                  type="button"
                  onClick={() => { onClose(); navigate('/app/community'); }}
                  className="text-xs mt-2 text-purple-600 hover:underline"
                  data-testid="share-community-discover-link"
                >
                  Découvrir les communautés →
                </button>
              </div>
            ) : (
              <>
                <select
                  value={selectedCommunityId}
                  onChange={(e) => setSelectedCommunityId(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-300 outline-none text-sm"
                  data-testid="share-community-select"
                  disabled={communityPosting || !!communityPostedTo}
                >
                  {myCommunities.map((c) => (
                    <option key={c.community_id} value={c.community_id}>
                      {c.name} ({c.member_count || 0} membres)
                    </option>
                  ))}
                </select>
                <textarea
                  value={communityMessage}
                  onChange={(e) => setCommunityMessage(e.target.value)}
                  placeholder={`Ex : Je viens de découvrir « ${shareTitle} », ça pourrait t'intéresser !`}
                  rows={3}
                  maxLength={500}
                  disabled={communityPosting || !!communityPostedTo}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-300 outline-none resize-none text-sm"
                  data-testid="share-community-message"
                />
                <p className="text-xs text-gray-400">
                  {communityMessage.length}/500 — Le lien et un aperçu seront ajoutés automatiquement.
                </p>
                <Button
                  onClick={handleShareToCommunity}
                  disabled={!selectedCommunityId || communityPosting || !!communityPostedTo}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                  data-testid="share-community-submit"
                >
                  {communityPosting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Publication…</>
                  ) : communityPostedTo ? (
                    <><Check className="h-4 w-4 mr-2" /> Publié dans la communauté</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Publier dans la communauté</>
                  )}
                </Button>
              </>
            )}
          </div>
        )}

        {/* Sprint 1.0c V1.a-bis hotfix #4 — Bug 7 : Actualité publish */}
        {activeTab === 'feed' && onShareToFeed && (          <div className="p-4 space-y-3" data-testid="share-feed-panel">
            <p className="text-sm text-gray-600">
              Publie un post dans le feed Actualité pour donner de la visibilité à ton événement.
            </p>
            <textarea
              value={actuMessage}
              onChange={(e) => setActuMessage(e.target.value)}
              placeholder={`📅 ${shareTitle}`}
              rows={3}
              maxLength={500}
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-300 outline-none resize-none text-sm"
              data-testid="share-feed-message"
            />
            <p className="text-xs text-gray-400">{actuMessage.length}/500 — Laisse vide pour utiliser un titre par défaut.</p>
            <Button
              onClick={async () => {
                setActuPosting(true);
                try {
                  await onShareToFeed(actuMessage.trim() || `📅 ${shareTitle}`);
                  setActuPosted(true);
                } finally {
                  setActuPosting(false);
                }
              }}
              disabled={actuPosting || actuPosted}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white"
              data-testid="share-feed-submit"
            >
              {actuPosting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Publication…</>
              ) : actuPosted ? (
                <><Check className="h-4 w-4 mr-2" /> Publié dans Actualité</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Publier dans Actualité</>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareModal;
